import { useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";
import { AmountFigure, AmountFigureDivider, type AmountFigureSize } from "./AmountFigure";
import { BanknoteButton } from "./BanknoteButton";
import { BANKNOTES, sanitizeReceivedInput } from "./CashChangePanel";
import { CLOSE_BOX_HEIGHT, CLOSE_BOX_RADIUS, CLOSE_ROW_GAP } from "./closeLayout";
import { GivenAmountField } from "./GivenAmountField";
import { keypadTextOf, useKeypadOpen } from "./keypadState";
import { MoneyKeypad } from "./MoneyKeypad";

/**
 * „В брой" с ресто, когато бакшишът е включен (дизайнът „Закрий"): горе
 * „Общо за плащане" и „Ресто" / „Остава" една до друга — празно поле или
 * „Точно" → ресто 0.00 (бледо); по-малко → „Остава" в червено; повече →
 * зеленото „Ресто", а сумата за плащане отстъпва назад. Под тях „Сума дадена от
 * клиента" с „Изчисти ×", а под полето — банкнотите (+ „Точно") или, при
 * разпъната клавиатура, екранните цифри (`MoneyKeypad`). В брой бакшиш няма
 * — само ресто; картата е тук само за бакшиша, записан при закриването
 * (повторен опит). Даденото е само помощ — не отива в заявката.
 *
 * Без бакшиш в настройките модалът ползва стария `CashChangePanel`.
 */
export function CashTipPanel({
  dueCents,
  received,
  onReceivedChange,
  tipCard,
  totalNote,
  figureSize,
  disabled = false,
}: {
  /** Дължимото в брой: поръчаното (+ записания бакшиш в брой при повторен опит). */
  dueCents: number;
  received: string;
  onReceivedChange: (value: string) => void;
  tipCard: ReactNode;
  totalNote?: ReactNode;
  figureSize: AmountFigureSize;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const keypad = useKeypadOpen();
  const receivedCents = received === "" ? 0 : (parseMoneyToCents(received) ?? 0);
  const differenceCents = receivedCents - dueCents;
  const isShort = receivedCents > 0 && differenceCents < 0;
  // Има ресто за връщане — „Точно" (ресто 0.00) е като празното поле.
  const hasChange = receivedCents > 0 && differenceCents > 0;

  const setReceived = (value: string) => {
    if (disabled) return;
    onReceivedChange(value);
    inputRef.current?.focus();
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex items-stretch">
        <AmountFigure
          label={t("closeTable.totalToPay")}
          cents={dueCents}
          size={figureSize}
          muted={hasChange}
          className={hasChange ? "text-pos-muted" : "text-pos-accent"}
        />
        <AmountFigureDivider size={figureSize} />
        {!isShort ? (
          <AmountFigure
            label={t("closeTable.changeShort")}
            cents={Math.max(0, differenceCents)}
            size={figureSize}
            className={hasChange ? "text-pos-served" : "text-pos-faint"}
          />
        ) : (
          <AmountFigure
            label={t("closeTable.remainingShort")}
            cents={-differenceCents}
            size={figureSize}
            className="text-danger-ink"
          />
        )}
      </div>
      {totalNote && (
        <div className="mt-1.5 text-[13px] font-medium leading-5 text-pos-soft2">{totalNote}</div>
      )}

      <div className="mt-5">
        <GivenAmountField
          label={t("closeTable.givenByClient")}
          value={received}
          placeholder="0.00"
          onChange={(raw) => onReceivedChange(sanitizeReceivedInput(raw))}
          onNormalize={() => {
            if (received !== "") onReceivedChange(formatCents(receivedCents));
          }}
          action={
            received === ""
              ? null
              : { label: t("closeTable.clearReceived"), onClick: () => setReceived("") }
          }
          keypad={keypad}
          isCompact={figureSize === "compact"}
          disabled={disabled}
          inputRef={inputRef}
          leaveOnArrows
        />
      </div>

      {keypad.isOpen ? (
        <div className="mt-3">
          <MoneyKeypad
            onKey={(key) => onReceivedChange(keypadTextOf(received, key))}
            disabled={disabled}
          />
        </div>
      ) : (
        <>
          <div className="mt-4 flex h-5 items-center text-[14px] font-bold text-pos-ink">
            {t("closeTable.banknotesTitle")}
          </div>
          {/* Банкнотите се ДОБАВЯТ към даденото; „Точно" — колкото е дължимото. */}
          <div className={`mt-2.5 grid grid-cols-3 ${CLOSE_ROW_GAP}`}>
            {BANKNOTES.map((banknote) => (
              <BanknoteButton
                key={banknote}
                banknote={banknote}
                disabled={disabled}
                onClick={() => setReceived(formatCents(receivedCents + banknote * 100))}
                className={`${CLOSE_BOX_HEIGHT} ${CLOSE_BOX_RADIUS} text-[17px]`}
              />
            ))}
            <button
              type="button"
              disabled={disabled}
              onClick={() => setReceived(formatCents(dueCents))}
              className={`${CLOSE_BOX_HEIGHT} ${CLOSE_BOX_RADIUS} cursor-pointer border border-pos-accent/50 bg-pos-card text-[15px] font-bold text-pos-accent-ink transition-colors hover:bg-pos-marked active:bg-pos-ready-bg disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {t("closeTable.exactAmount")}
            </button>
          </div>
        </>
      )}

      {/* empty:hidden — записан бакшиш има само при повторен опит */}
      <div className="mt-5 empty:hidden">{tipCard}</div>
    </div>
  );
}
